import { forwardRef, useCallback, useState } from "react";

import { useController } from "react-hook-form";

import { FILTER } from "@/constants";
import { FilterOption } from "@/types/FilterOptions";
import { Checkbox, Input } from "@components/Field";
import { ListItem } from "@components/List";
import Typography from "@components/Typography";

import styles from "./ChannelName.module.css";

interface CustomMultiSelectProps extends React.HTMLProps<HTMLInputElement> {
  options?: FilterOption[];
}

function pack<T>(value: T[] | T): T[] {
  return Array.isArray(value) ? value : [value];
}

const MAX_RENDER_SIZE = 25;

const ChannelNameSearch = forwardRef<HTMLInputElement, CustomMultiSelectProps>(
  ({ options = [] }: CustomMultiSelectProps) => {
    const {
      field: { value = [], onChange, onBlur },
    } = useController({ name: FILTER.CHANNEL_NAME });

    const [opts, setOpts] = useState<FilterOption[]>(options || []);

    const handleFilterOptions = useCallback(
      (term?: string, values: string[] = []) => {
        const filter = term?.toLowerCase() || "";

        const picks: FilterOption[] = [];
        const nonpicks: FilterOption[] = [];

        if (filter === "") {
          // Get the current picks along with their labels

          options.forEach((o) => {
            const [v] = o;
            values?.includes(v) ? picks.push(o) : nonpicks.push(o);
          });

          setOpts([...picks, ...nonpicks]);
          return;
        }
        // Only filter the currently not picked.
        const filtered = options.filter(([v, l]) => {
          return (
            v.toLowerCase().includes(filter) ||
            l?.toLowerCase().includes(filter)
          );
        });

        // Add the picks to the top of the list and set the new result set.
        setOpts(filtered);
      },
      [options]
    );

    const renderableOptions = opts.slice(0, MAX_RENDER_SIZE);

    return (
      <div>
        <Input
          className={styles.input}
          placeholder={L.MENTIONS_VIEW_TOOLBAR_FILTER_CHANNEL_NAME_SEARCH_PLACEHOLDER.replace(
            "{}",
            (options.length || 0).toString()
          )}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFilterOptions(e.currentTarget.value, pack(value))
          }
          block
        />
        <div className={styles.results}>
          {renderableOptions.length === 0 && (
            <ListItem>
              {L.MENTIONS_VIEW_TOOLBAR_FILTER_CHANNEL_NAME_SEARCH_NO_RESULTS}
            </ListItem>
          )}
          {renderableOptions.map(([itemValue, itemLabel]) => (
            <ListItem key={itemValue} active={Math.random() > 0.6} passPadding>
              <Checkbox
                value={itemValue}
                checked={value.includes(itemValue)}
                onChange={(e) => {
                  if (e.currentTarget.checked) {
                    onChange([...value, itemValue]);
                  } else {
                    onChange(value.filter((v: string) => v !== itemValue));
                  }
                }}
                label={itemLabel || itemValue}
                onBlur={onBlur}
              />
            </ListItem>
          ))}
          {renderableOptions.length === MAX_RENDER_SIZE && (
            <Typography variant="caption" className={styles.cap} block>
              {L.MENTIONS_VIEW_TOOLBAR_FILTER_CHANNEL_NAME_SEARCH_CAP_INFO.replace(
                "{}",
                MAX_RENDER_SIZE.toString()
              )}
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

ChannelNameSearch.displayName = "ChannelNameSearch";

export default ChannelNameSearch;
